package com.merkost.handpass.sharedweb.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLElement
import org.w3c.dom.Node
import org.w3c.dom.asList
import org.w3c.dom.events.Event
import org.w3c.dom.events.EventListener

private const val WebFocusableSelector =
    "a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex=\"-1\"])"

private var webBodyLockDepth = 0
private var webBodyPreviousOverflow = ""

private data class WebDialogIsolationState(
    val ariaHidden: String?,
    val inert: Boolean,
)

private fun HTMLElement.focusableDescendants(): List<HTMLElement> =
    querySelectorAll(WebFocusableSelector).asList().filterIsInstance<HTMLElement>()

private fun acquireWebBodyLock() {
    if (webBodyLockDepth == 0) {
        val body = document.body
        webBodyPreviousOverflow = body?.style?.getPropertyValue("overflow") ?: ""
        body?.style?.setProperty("overflow", "hidden")
    }
    webBodyLockDepth += 1
}

private fun releaseWebBodyLock() {
    webBodyLockDepth = (webBodyLockDepth - 1).coerceAtLeast(0)
    if (webBodyLockDepth == 0) {
        document.body?.style?.setProperty("overflow", webBodyPreviousOverflow)
    }
}

private fun setWebDialogBackgroundIsolation(container: HTMLElement?): Map<HTMLElement, WebDialogIsolationState> {
    val changed = linkedMapOf<HTMLElement, WebDialogIsolationState>()
    var child: Node? = container
    while (child?.parentNode != null) {
        val parent = child.parentNode ?: break
        parent.childNodes.asList().filter { it != child }.filterIsInstance<HTMLElement>().forEach { sibling ->
            changed[sibling] = WebDialogIsolationState(
                ariaHidden = sibling.getAttribute("aria-hidden"),
                inert = (sibling.asDynamic().inert as? Boolean) ?: false,
            )
            sibling.setAttribute("aria-hidden", "true")
            sibling.asDynamic().inert = true
        }
        if (parent == document.body) break
        child = parent
    }
    return changed
}

@Composable
fun WebDialogFocusScope(container: HTMLElement?, onDismiss: () -> Unit) {
    val currentContainer by rememberUpdatedState(container)
    val currentDismiss by rememberUpdatedState(onDismiss)
    val previouslyFocused = remember { mutableStateOf<HTMLElement?>(null) }

    DisposableEffect(Unit) {
        acquireWebBodyLock()
        val handler = EventListener { event: Event ->
            val key = (event.asDynamic().key as? String) ?: ""
            val activeContainer = currentContainer
            if (key == "Escape") {
                currentDismiss()
            } else if (key == "Tab" && activeContainer != null) {
                val focusables = activeContainer.focusableDescendants()
                if (focusables.isEmpty()) {
                    event.preventDefault()
                    activeContainer.focus()
                } else {
                    val first = focusables.first()
                    val last = focusables.last()
                    val active = document.activeElement
                    val shift = (event.asDynamic().shiftKey as? Boolean) ?: false
                    if (shift && active == first) {
                        event.preventDefault()
                        last.focus()
                    } else if (!shift && active == last) {
                        event.preventDefault()
                        first.focus()
                    }
                }
            }
        }
        window.addEventListener("keydown", handler)
        onDispose {
            window.removeEventListener("keydown", handler)
            releaseWebBodyLock()
            previouslyFocused.value?.focus()
        }
    }

    DisposableEffect(container) {
        val isolatedElements = setWebDialogBackgroundIsolation(container)
        if (container != null) {
            if (previouslyFocused.value == null) {
                previouslyFocused.value = document.activeElement as? HTMLElement
            }
            (container.focusableDescendants().firstOrNull() ?: container).focus()
        }
        onDispose {
            isolatedElements.forEach { (element, previous) ->
                if (previous.ariaHidden == null) element.removeAttribute("aria-hidden") else element.setAttribute("aria-hidden", previous.ariaHidden)
                element.asDynamic().inert = previous.inert
            }
        }
    }
}
